<template>
  <div :class="['toast-container', position]">
    <CustomToast
      v-for="toast in toasts"
      :key="toast.id"
      :type="toast.type"
      :message="toast.message"
      :subtitle="toast.subtitle"
      :duration="toast.duration"
      :closable="toast.closable"
      @close="removeToast(toast.id)"
      @click="toast.onClick && toast.onClick()"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import CustomToast from './CustomToast.vue'

const props = defineProps({
  position: {
    type: String,
    default: 'bottom-left',
  },
})

const toasts = ref([])
let toastIdCounter = 0

const addToast = (toastData) => {
  const id = ++toastIdCounter
  const toast = {
    id,
    type: toastData.type || 'info',
    message: toastData.message,
    subtitle: toastData.subtitle || '',
    duration: toastData.duration || 4000,
    closable: toastData.closable !== false,
    onClick: toastData.onClick,
  }

  toasts.value.push(toast)

  // Auto remove after duration
  if (toast.duration > 0) {
    setTimeout(() => {
      removeToast(id)
    }, toast.duration)
  }

  return id
}

const removeToast = (id) => {
  const index = toasts.value.findIndex((toast) => toast.id === id)
  if (index > -1) {
    toasts.value.splice(index, 1)
  }
}

const clearAll = () => {
  toasts.value = []
}

// Global toast functions
const success = (message, options = {}) => {
  return addToast({ type: 'success', message, ...options })
}

const error = (message, options = {}) => {
  return addToast({ type: 'error', message, ...options })
}

const info = (message, options = {}) => {
  return addToast({ type: 'info', message, ...options })
}

const warning = (message, options = {}) => {
  return addToast({ type: 'warning', message, ...options })
}

const preparing = (message, options = {}) => {
  return addToast({ type: 'preparing', message, ...options })
}

// Expose methods globally
onMounted(() => {
  window.customToast = {
    success,
    error,
    info,
    warning,
    preparing,
    add: addToast,
    remove: removeToast,
    clear: clearAll,
  }
})

onUnmounted(() => {
  if (window.customToast) {
    delete window.customToast
  }
})

defineExpose({
  addToast,
  removeToast,
  clearAll,
  success,
  error,
  info,
  warning,
  preparing,
})
</script>

<style scoped>
.toast-container {
  position: fixed;
  z-index: 9999;
  pointer-events: none;
  max-width: 500px;
}

.toast-container.top-left {
  top: 20px;
  left: 20px;
}

.toast-container.top-right {
  top: 20px;
  right: 20px;
}

.toast-container.bottom-left {
  bottom: 20px;
  left: 20px;
}

.toast-container.bottom-right {
  bottom: 20px;
  right: 20px;
}

.toast-container.top-center {
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
}

.toast-container.bottom-center {
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
}
</style>
